import React, { useState, useEffect } from 'react';
import { adminClient } from '@/api/adminClient';
import type { GlossaryTerm } from '@/types';

export default function GlossaryManager() {
  const [terms, setTerms] = useState<GlossaryTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTerm, setEditingTerm] = useState<GlossaryTerm | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    loadGlossary();
  }, []);

  const loadGlossary = async () => {
    try {
      const data = await adminClient.getGlossary();
      setTerms(data);
    } catch (err) {
      console.error('Failed to load glossary:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const result = await adminClient.uploadGlossary(file);
      alert(`Imported ${result.imported} terms. ${result.errors.length} errors.`);
      loadGlossary();
    } catch (err) {
      console.error('Failed to upload glossary:', err);
      alert('Failed to upload glossary');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await adminClient.exportGlossary();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `glossary-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export glossary:', err);
      alert('Failed to export glossary');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this term?')) return;

    try {
      await adminClient.deleteGlossaryTerm(id);
      setTerms((prev) => prev.filter((term) => term.id !== id));
    } catch (err) {
      console.error('Failed to delete term:', err);
      alert('Failed to delete term');
    }
  };

  const filteredTerms = terms.filter(
    (term) =>
      term.source.toLowerCase().includes(searchQuery.toLowerCase()) ||
      term.target.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.loadingText}>Loading glossary...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Medical Glossary</h1>
          <p style={styles.subtitle}>Manage translation terms and definitions</p>
        </div>
        <div style={styles.actions}>
          <label style={styles.uploadButton}>
            Upload CSV
            <input
              type="file"
              accept=".csv"
              onChange={handleUpload}
              style={styles.fileInput}
            />
          </label>
          <button onClick={handleExport} style={styles.exportButton}>
            Export CSV
          </button>
          <button onClick={() => setShowAddForm(true)} style={styles.addButton}>
            Add Term
          </button>
        </div>
      </div>

      <div style={styles.searchBar}>
        <input
          type="text"
          placeholder="Search terms..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
        <span style={styles.resultCount}>
          {filteredTerms.length} of {terms.length} terms
        </span>
      </div>

      {showAddForm && (
        <AddTermForm
          onClose={() => setShowAddForm(false)}
          onSuccess={(term) => {
            setTerms((prev) => [term, ...prev]);
            setShowAddForm(false);
          }}
        />
      )}

      {editingTerm && (
        <EditTermForm
          term={editingTerm}
          onClose={() => setEditingTerm(null)}
          onSuccess={(updated) => {
            setTerms((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
            setEditingTerm(null);
          }}
        />
      )}

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>Source</th>
              <th style={styles.th}>Target</th>
              <th style={styles.th}>Language</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Updated</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTerms.map((term) => (
              <tr key={term.id} style={styles.row}>
                <td style={styles.td}>{term.source}</td>
                <td style={styles.td}>{term.target}</td>
                <td style={styles.td}>
                  <span style={styles.languageBadge}>{term.language}</span>
                </td>
                <td style={styles.td}>{term.category || '—'}</td>
                <td style={styles.td}>
                  {new Date(term.updatedAt).toLocaleDateString()}
                </td>
                <td style={styles.td}>
                  <div style={styles.actionButtons}>
                    <button
                      onClick={() => setEditingTerm(term)}
                      style={styles.editButton}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(term.id)}
                      style={styles.deleteButton}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface AddTermFormProps {
  onClose: () => void;
  onSuccess: (term: GlossaryTerm) => void;
}

function AddTermForm({ onClose, onSuccess }: AddTermFormProps) {
  const [source, setSource] = useState('');
  const [target, setTarget] = useState('');
  const [language, setLanguage] = useState('ko');
  const [category, setCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const term = await adminClient.createGlossaryTerm({
        source,
        target,
        language,
        category: category || undefined,
      });
      onSuccess(term);
    } catch (err) {
      console.error('Failed to create term:', err);
      alert('Failed to create term');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <h2 style={styles.modalTitle}>Add New Term</h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Source Term</label>
            <input
              type="text"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              required
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Target Translation</label>
            <input
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              required
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Language</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              style={styles.select}
            >
              <option value="ko">Korean</option>
              <option value="en">English</option>
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Category (Optional)</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.formActions}>
            <button type="button" onClick={onClose} style={styles.cancelButton}>
              Cancel
            </button>
            <button type="submit" disabled={submitting} style={styles.submitButton}>
              {submitting ? 'Adding...' : 'Add Term'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface EditTermFormProps {
  term: GlossaryTerm;
  onClose: () => void;
  onSuccess: (term: GlossaryTerm) => void;
}

function EditTermForm({ term, onClose, onSuccess }: EditTermFormProps) {
  const [source, setSource] = useState(term.source);
  const [target, setTarget] = useState(term.target);
  const [category, setCategory] = useState(term.category || '');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const updated = await adminClient.updateGlossaryTerm(term.id, {
        source,
        target,
        category: category || undefined,
      });
      onSuccess(updated);
    } catch (err) {
      console.error('Failed to update term:', err);
      alert('Failed to update term');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <h2 style={styles.modalTitle}>Edit Term</h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Source Term</label>
            <input
              type="text"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              required
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Target Translation</label>
            <input
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              required
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Category (Optional)</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.formActions}>
            <button type="button" onClick={onClose} style={styles.cancelButton}>
              Cancel
            </button>
            <button type="submit" disabled={submitting} style={styles.submitButton}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1a202c',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '16px',
    color: '#718096',
    margin: 0,
  },
  actions: {
    display: 'flex',
    gap: '12px',
  },
  uploadButton: {
    padding: '10px 20px',
    backgroundColor: '#38b2ac',
    color: 'white',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  fileInput: {
    display: 'none',
  },
  exportButton: {
    padding: '10px 20px',
    backgroundColor: '#4299e1',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#48bb78',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  searchInput: {
    flex: 1,
    padding: '12px 16px',
    border: '1px solid #cbd5e0',
    borderRadius: '8px',
    fontSize: '14px',
  },
  resultCount: {
    fontSize: '14px',
    color: '#718096',
    fontWeight: '500',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {
    backgroundColor: '#f7fafc',
  },
  th: {
    padding: '12px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '600',
    color: '#4a5568',
    textTransform: 'uppercase',
    borderBottom: '2px solid #e2e8f0',
  },
  row: {
    borderBottom: '1px solid #e2e8f0',
  },
  td: {
    padding: '12px',
    fontSize: '14px',
    color: '#2d3748',
  },
  languageBadge: {
    padding: '4px 10px',
    backgroundColor: '#e6fffa',
    color: '#234e52',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  editButton: {
    padding: '6px 12px',
    backgroundColor: '#4299e1',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  deleteButton: {
    padding: '6px 12px',
    backgroundColor: '#f56565',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  loading: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: '18px',
    color: '#4a5568',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '32px',
    width: '100%',
    maxWidth: '500px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1a202c',
    margin: '0 0 24px 0',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#2d3748',
  },
  input: {
    padding: '10px 12px',
    border: '1px solid #cbd5e0',
    borderRadius: '6px',
    fontSize: '14px',
  },
  select: {
    padding: '10px 12px',
    border: '1px solid #cbd5e0',
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: 'white',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
  },
  cancelButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: '#e2e8f0',
    color: '#2d3748',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: '#48bb78',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};
